import {
  Badge,
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';

import { messagingApi, MessagingIntegration } from 'apis/messaging';
import { appActions } from 'app/app.actions';
import { MessagingQueueDetails } from 'app/views/layout/views/settings/views/messaging/components/queueDetails/messagingQueueDetails';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { WhatsAppEditIntegrationModal } from './components/whatsAppEditIntegrationModal/whatsAppEditIntegrationModal';

export const WhatsApp = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const { integrations, isFetching } =
    messagingApi.useReadAppsAndIntegrationsQuery(undefined, {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        integrations: data?.data.integrations ?? [],
      }),
    });

  const [deleteIntegration] = messagingApi.useDeleteIntegrationMutation();

  const handleEdit = (integration: MessagingIntegration) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:whatsAppEditIntegration',
          arg: { integration },
        },
      }),
    );
  };

  const handleRemove = async (integration: MessagingIntegration) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Remove "${integration.integration_name}"`,
            description:
              'Are you sure you want to remove your WhatsApp number from Dixa?',
            confirm: {
              label: 'Remove',
              async: true,
              asyncLabel: 'Removing...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        confirm(async () => {
          await deleteIntegration({
            appId: integration.app_id,
            integrationId: integration.integration_id,
          }).unwrap();

          notify(
            {
              title: 'WhatsApp number successfully removed.',
              time: 3000,
            },
            NotificationType.Success,
          );
        });
      } catch {
        notify(
          {
            title: 'An error occurred while removing the WhatsApp number.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  const handleAddNumber = () => {
    navigate({ path: `/settings/messaging/whatsapp/setup` });
  };

  return (
    <>
      <Page.Section
        title="WhatsApp"
        controls={
          <Button variant="secondary" icon="plus" onClick={handleAddNumber}>
            WhatsApp number
          </Button>
        }
      >
        <DataTable
          data={integrations}
          columns={[
            {
              label: 'Phone number',
              selector: ({ integration, phone_number }) => (
                <FlexBox alignItems="center" gap="300">
                  {phone_number}

                  {!integration && (
                    <Tooltip
                      content="This can take up to 4-6 weeks. We'll send out an email as soon
      as the approval goes through."
                      dark
                    >
                      <Badge variant="warning" size="medium">
                        Waiting for approval
                      </Badge>
                    </Tooltip>
                  )}
                </FlexBox>
              ),
              style: {
                width: '30%',
              },
            },
            {
              label: 'Name',
              selector: ({ integration }) =>
                integration ? integration.integration_name : null,
              style: {
                width: '30%',
              },
            },
            {
              label: 'Queue',
              selector: ({ integration }) =>
                integration ? (
                  <MessagingQueueDetails queueId={integration.queue_id} />
                ) : null,
              style: {
                width: '30%',
              },
            },
            {
              label: '',
              selector: ({ integration }) =>
                integration ? (
                  <FlexBox justifyContent="flex-end">
                    <IconButton
                      variant="tertiary"
                      icon="pen"
                      onClick={() => handleEdit(integration)}
                    />

                    <IconButton
                      variant="tertiary"
                      icon="trash"
                      onClick={() => handleRemove(integration)}
                    />
                  </FlexBox>
                ) : null,
              style: {
                width: 0,
              },
            },
          ]}
          tableStatusProps={{
            loading: isFetching,
            ...(!isFetching &&
              !integrations.length && {
                emptyState: {
                  icon: 'whatsapp',
                  title: 'Get started with WhatsApp',
                  description:
                    'Click the "+ WhatsApp number" button above and we\'ll help you through the setup process of the WhatsApp channel.',
                  buttonProps: {
                    children: 'Learn more',
                    variant: 'secondary',
                    onClick: () => elevioClient.openArticle('176'),
                  },
                },
              }),
          }}
        />
      </Page.Section>

      <WhatsAppEditIntegrationModal />
    </>
  );
};
